// PRD.313 — composition rules for the longitudinal history surface.
//
// Pure and DOM-free so the ordering / empty-state / labelling decisions are
// unit-testable under the repo's node:test harness. The date formatter is
// injected rather than imported, keeping this module free of the React and
// PowerSync imports that live alongside `formatVisitDate`.

/** One read-only historical entry, as the presentation component consumes it. */
export interface LongitudinalEntryInput {
  encounterId: string;
  visitDate: string | null;
  text: string;
}

export interface LongitudinalEntryView {
  id: string;
  label: string;
  text: string;
}

/**
 * Map selected prior entries onto the generic presentation shape.
 *
 * Order is PRESERVED exactly — `selectPriorMedicalHistory` already returns
 * oldest → newest (PRD.313 §5.2), and re-sorting here would put two competing
 * chronologies in the codebase.
 */
export function toLongitudinalEntries(
  entries: readonly LongitudinalEntryInput[],
  formatLabel: (date: string | null) => string,
): LongitudinalEntryView[] {
  return entries.map((e) => ({
    id: e.encounterId,
    label: formatLabel(e.visitDate),
    text: e.text,
  }));
}

/**
 * Whether to render the historical region at all.
 *
 * False both when the clinic switched history off (PRD §9) and when there is
 * genuinely nothing to show (PRD §11.1) — in either case the surface is just
 * the current editor rather than an empty historical shell.
 */
export function shouldRenderHistory(
  showHistory: boolean,
  patientRecord: string | null,
  entries: readonly unknown[],
): boolean {
  return showHistory && (Boolean(patientRecord) || entries.length > 0);
}
